import './success-rate-chart.css';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetTransmissionStatsQuery } from '../../redux/api/transmission-logs';
import { TransmissionStats } from '../../model/transmission-stats-model';

const RANGE_OPTIONS = [
    { label: '7D', days: 7 },
    { label: '30D', days: 30 },
    { label: '90D', days: 90 },
];

const PADDING = { top: 16, right: 12, bottom: 28, left: 34 };
const Y_TICKS = [0, 50, 100];
const SUCCESS_RATE_ALERT_THRESHOLD = 50;

const toIsoDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatShortDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const useContainerSize = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!ref.current) return;
        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            if (entry) {
                const { width, height } = entry.contentRect;
                setSize({ width, height });
            }
        });
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return { ref, size };
};

const SuccessRateChart = () => {

    const [rangeDays, setRangeDays] = useState(30);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const { ref: plotRef, size } = useContainerSize();

    const { startDate, endDate } = useMemo(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - (rangeDays - 1));
        return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
    }, [rangeDays]);

    const { data, isFetching } = useGetTransmissionStatsQuery({ startDate, endDate });

    const [renderedData, setRenderedData] = useState<TransmissionStats[]>([]);
    useEffect(() => {
        if (data) {
            setRenderedData(data);
        }
    }, [data]);

    const plotWidth = Math.max(0, size.width - PADDING.left - PADDING.right);
    const plotHeight = Math.max(0, size.height - PADDING.top - PADDING.bottom);
    const isMeasured = size.width > 0 && size.height > 0;

    const yForRate = (rate: number) => PADDING.top + (1 - rate / 100) * plotHeight;
    const xForIndex = (index: number, count: number) =>
        PADDING.left + (count <= 1 ? 0 : (index / (count - 1)) * plotWidth);

    const points = renderedData.map((stat, index) => ({
        ...stat,
        x: xForIndex(index, renderedData.length),
        y: stat.successRate !== null ? yForRate(stat.successRate) : null,
    }));

    const segments: (typeof points)[] = [];
    let current: typeof points = [];
    points.forEach(point => {
        if (point.y === null) {
            if (current.length) segments.push(current);
            current = [];
        } else {
            current.push(point);
        }
    });
    if (current.length) segments.push(current);

    const totals = renderedData.reduce(
        (acc, stat) => ({
            success: acc.success + stat.successCount,
            total: acc.total + stat.successCount + stat.failCount,
        }),
        { success: 0, total: 0 }
    );
    const heroRate = totals.total > 0 ? Math.round((totals.success / totals.total) * 1000) / 10 : null;
    const isBelowThreshold = heroRate !== null && heroRate < SUCCESS_RATE_ALERT_THRESHOLD;
    const seriesColor = isBelowThreshold ? 'var(--src-bad)' : 'var(--src-good)';
    const seriesWashColor = isBelowThreshold ? 'var(--src-bad-wash)' : 'var(--src-good-wash)';

    const handlePointerMove = (event: React.PointerEvent<SVGRectElement>) => {
        if (points.length === 0 || plotWidth === 0) return;
        const rect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
        if (!rect) return;
        const relativeX = (event.clientX - rect.left) - PADDING.left;
        const ratio = Math.min(1, Math.max(0, relativeX / plotWidth));
        const index = Math.round(ratio * (points.length - 1));
        setHoverIndex(index);
    };

    const hovered = hoverIndex !== null ? points[hoverIndex] : null;

    return (
        <div className="success-rate-chart">
            <div className="src-header">
                <span>Transmission Success Rate</span>
                <div className="src-range-toggle">
                    {RANGE_OPTIONS.map(option => (
                        <button
                            key={option.days}
                            className={option.days === rangeDays ? 'active' : ''}
                            onClick={() => setRangeDays(option.days)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="src-hero">
                <span className={`src-hero-value${isBelowThreshold ? ' is-bad' : ''}`}>{heroRate !== null ? `${heroRate}%` : '—'}</span>
                <span className="src-hero-sub">
                    {totals.total > 0
                        ? `${totals.success.toLocaleString()} of ${totals.total.toLocaleString()} transmissions, last ${rangeDays} days`
                        : `No transmissions in the last ${rangeDays} days`}
                </span>
            </div>
            <div ref={plotRef} className={`src-plot${isFetching ? ' is-fetching' : ''}`}>
                {renderedData.length === 0 && !isFetching && isMeasured && (
                    <div className="src-empty">No data available</div>
                )}
                {renderedData.length > 0 && isMeasured && (
                    <svg viewBox={`0 0 ${size.width} ${size.height}`}>
                        {Y_TICKS.map(tick => (
                            <g key={tick}>
                                <line
                                    x1={PADDING.left}
                                    x2={size.width - PADDING.right}
                                    y1={yForRate(tick)}
                                    y2={yForRate(tick)}
                                    stroke="var(--src-grid)"
                                    strokeWidth={1}
                                />
                                <text
                                    x={PADDING.left - 8}
                                    y={yForRate(tick)}
                                    textAnchor="end"
                                    dominantBaseline="middle"
                                    fontSize={10}
                                    fill="var(--src-text-muted)"
                                >
                                    {tick}%
                                </text>
                            </g>
                        ))}

                        {points.length > 0 && (
                            <>
                                <text x={PADDING.left} y={size.height - 8} fontSize={10} fill="var(--src-text-muted)">
                                    {formatShortDate(points[0].date)}
                                </text>
                                <text
                                    x={size.width - PADDING.right}
                                    y={size.height - 8}
                                    textAnchor="end"
                                    fontSize={10}
                                    fill="var(--src-text-muted)"
                                >
                                    {formatShortDate(points[points.length - 1].date)}
                                </text>
                            </>
                        )}

                        {segments.map((segment, segmentIndex) => (
                            <g key={segmentIndex}>
                                <polygon
                                    points={
                                        `${segment[0].x},${yForRate(0)} ` +
                                        segment.map(p => `${p.x},${p.y}`).join(' ') +
                                        ` ${segment[segment.length - 1].x},${yForRate(0)}`
                                    }
                                    fill={seriesWashColor}
                                />
                                <polyline
                                    points={segment.map(p => `${p.x},${p.y}`).join(' ')}
                                    fill="none"
                                    stroke={seriesColor}
                                    strokeWidth={2}
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                />
                            </g>
                        ))}

                        {points.length > 0 && points[points.length - 1].y !== null && (
                            <>
                                <circle
                                    cx={points[points.length - 1].x}
                                    cy={points[points.length - 1].y as number}
                                    r={4}
                                    fill={seriesColor}
                                    stroke="var(--src-surface)"
                                    strokeWidth={2}
                                />
                                <text
                                    x={points[points.length - 1].x - 6}
                                    y={
                                        (points[points.length - 1].y as number) - PADDING.top < 14
                                            ? (points[points.length - 1].y as number) + 16
                                            : (points[points.length - 1].y as number) - 10
                                    }
                                    textAnchor="end"
                                    fontSize={11}
                                    fontWeight={600}
                                    fill="var(--src-text-primary)"
                                >
                                    {points[points.length - 1].successRate}%
                                </text>
                            </>
                        )}

                        {hovered && (
                            <line
                                x1={hovered.x}
                                x2={hovered.x}
                                y1={PADDING.top}
                                y2={size.height - PADDING.bottom}
                                stroke="var(--src-axis)"
                                strokeWidth={1}
                            />
                        )}
                        {hovered && hovered.y !== null && (
                            <circle cx={hovered.x} cy={hovered.y} r={4} fill={seriesColor} stroke="var(--src-surface)" strokeWidth={2} />
                        )}

                        <rect
                            x={PADDING.left}
                            y={PADDING.top}
                            width={plotWidth}
                            height={plotHeight}
                            fill="transparent"
                            onPointerMove={handlePointerMove}
                            onPointerLeave={() => setHoverIndex(null)}
                        />
                    </svg>
                )}
                {hovered && size.width > 0 && (
                    <div
                        className="src-tooltip"
                        style={{ left: `${(hovered.x / size.width) * 100}%` }}
                    >
                        <div className="src-tooltip-date">{formatShortDate(hovered.date)}</div>
                        <div className="src-tooltip-rate">
                            <span className="src-key" style={{ background: seriesColor }} />
                            {hovered.successRate !== null ? `${hovered.successRate}%` : 'No data'}
                        </div>
                        <div className="src-tooltip-counts">
                            {hovered.successCount.toLocaleString()} succeeded, {hovered.failCount.toLocaleString()} failed
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuccessRateChart;
